import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import UsernameSetupModal from '../UsernameSetupModal';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact Dashboard panel summarising Wanted List + Matches activity.
 * - Shows active-wants count + unread-matches badge
 * - Quick "Add a title" shortcut that opens /wanted-list
 * - Prompts to set username if not yet set
 */
export default function WantedListPanel({ user }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ wants: 0, unread: 0 });
  const [needsUsername, setNeedsUsername] = useState(false);
  const [showUsername, setShowUsername] = useState(false);

  const refresh = async () => {
    try {
      const [wantsRes, matchesRes] = await Promise.all([
        fetch(`${API}/api/wanted-list/my`, { credentials: 'include' }),
        fetch(`${API}/api/wanted-list/matches/my`, { credentials: 'include' }),
      ]);
      const wants = wantsRes.ok ? await wantsRes.json() : { count: 0 };
      const matches = matchesRes.ok ? await matchesRes.json() : { unread: 0 };
      setStats({ wants: wants.count || 0, unread: matches.unread || 0 });
    } catch (err) {
      // Panel is a best-effort dashboard widget — swallowing the error keeps the rest
      // of the dashboard usable. Log for observability.
      // eslint-disable-next-line no-console
      console.warn('WantedListPanel refresh failed', err);
    }
  };

  useEffect(() => {
    if (user && !user.username) setNeedsUsername(true);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const onAddClick = () => {
    if (needsUsername) {
      setShowUsername(true);
    } else {
      navigate('/wanted-list');
    }
  };

  return (
    <>
      <div
        className="bg-zinc-900 border border-zinc-800 rounded-lg p-5 mb-6"
        data-testid="wanted-list-panel"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xl">⚡</span>
              <h3 className="text-lg font-bold text-white">Community Match</h3>
              {stats.unread > 0 && (
                <span
                  className="text-[10px] font-black px-2 py-0.5 rounded-full"
                  style={{ background: '#FFD700', color: '#050505' }}
                  data-testid="unread-matches-badge"
                >
                  {stats.unread} NEW
                </span>
              )}
            </div>
            <p className="text-sm text-zinc-400 mt-1">
              {stats.wants === 0
                ? 'Add titles you want — we alert you when a seller scans one below your max price.'
                : `${stats.wants} active want${stats.wants === 1 ? '' : 's'} · ${stats.unread} unread match${stats.unread === 1 ? '' : 'es'}`}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={onAddClick}
              className="px-4 py-2 rounded-full font-bold text-sm"
              style={{ background: 'linear-gradient(90deg,#00D26A,#FFD700)', color: '#050505' }}
              data-testid="add-wanted-btn"
            >
              + Add a title
            </button>
            <button
              onClick={() => navigate('/matches')}
              className="px-4 py-2 rounded-full font-bold text-sm border border-zinc-700 text-zinc-200 hover:bg-zinc-800"
              data-testid="view-matches-btn"
            >
              View matches
            </button>
          </div>
        </div>
      </div>

      {showUsername && (
        <UsernameSetupModal
          onClose={() => setShowUsername(false)}
          onSuccess={() => {
            setShowUsername(false);
            setNeedsUsername(false);
            navigate('/wanted-list');
          }}
        />
      )}
    </>
  );
}
